import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../common/Card';
import { Button } from '../common/Button';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface EditInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number | null;
  onInvoiceUpdated: () => void;
}

interface Invoice {
  id: number;
  invoice_number: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  client_address?: string;
  client_tax_id?: string;
  additional_emails: string[];
  amount_net: number;
  amount_tax: number;
  amount_gross: number;
  currency: string;
  issue_date: string;
  due_date: string;
  payment_date?: string;
  status: string;
  description?: string;
  notes?: string;
}

export const EditInvoiceModal = ({ isOpen, onClose, invoiceId, onInvoiceUpdated }: EditInvoiceModalProps) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    if (isOpen && invoiceId) {
      fetchInvoice();
    }
  }, [isOpen, invoiceId]);

  const fetchInvoice = async () => {
    if (!invoiceId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(`/invoices/${invoiceId}`);
      const invoiceData = response.data;
      setInvoice(invoiceData);
      setAdditionalEmails(invoiceData.additional_emails || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch invoice');
      console.error('Error fetching invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof Invoice, value: string | number) => {
    if (!invoice) return;
    
    let updatedInvoice = { ...invoice, [field]: value };
    
    // Automatically set payment_date when status changes to "paid"
    if (field === 'status' && value === 'paid' && !invoice.payment_date) {
      updatedInvoice.payment_date = new Date().toISOString().split('T')[0];
    }
    
    // Clear payment_date when status changes from "paid" to something else
    if (field === 'status' && value !== 'paid' && invoice.payment_date) {
      updatedInvoice.payment_date = '';
    }
    
    setInvoice(updatedInvoice);
  };

  const addEmail = () => {
    if (newEmail.trim() && isValidEmail(newEmail.trim())) {
      if (!additionalEmails.includes(newEmail.trim())) {
        setAdditionalEmails([...additionalEmails, newEmail.trim()]);
        setNewEmail('');
      }
    }
  };

  const removeEmail = (index: number) => {
    setAdditionalEmails(additionalEmails.filter((_, i) => i !== index));
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = async () => {
    if (!invoice) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const updateData = {
        client_name: invoice.client_name,
        client_email: invoice.client_email,
        client_phone: invoice.client_phone,
        client_address: invoice.client_address,
        client_tax_id: invoice.client_tax_id,
        additional_emails: additionalEmails,
        status: invoice.status,
        payment_date: invoice.payment_date,
        description: invoice.description,
        notes: invoice.notes
      };

      await apiService.put(`/invoices/${invoice.id}`, updateData);
      onInvoiceUpdated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update invoice');
      console.error('Error updating invoice:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Edytuj fakturę</CardTitle>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Ładowanie faktury...</span>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={fetchInvoice} variant="outline">
                  Spróbuj ponownie
                </Button>
              </div>
            ) : invoice ? (
              <div className="space-y-6">
                {/* Basic Invoice Info */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informacje podstawowe</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Numer faktury
                      </label>
                      <input
                        type="text"
                        value={invoice.invoice_number}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={invoice.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="draft">Projekt</option>
                        <option value="sent">Wysłana</option>
                        <option value="paid">Opłacona</option>
                        <option value="overdue">Przeterminowana</option>
                        <option value="cancelled">Anulowana</option>
                      </select>
                    </div>
                    {invoice.status === 'paid' && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-green-700">
                              <strong>Faktura oznaczona jako opłacona</strong>
                              {invoice.payment_date && (
                                <span> - Data płatności: {new Date(invoice.payment_date).toLocaleDateString('pl-PL')}</span>
                              )}
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                              Przypomnienia o płatności nie będą wysyłane dla tej faktury.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Dane klienta</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nazwa klienta *
                      </label>
                      <input
                        type="text"
                        value={invoice.client_name}
                        onChange={(e) => handleInputChange('client_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email główny *
                      </label>
                      <input
                        type="email"
                        value={invoice.client_email}
                        onChange={(e) => handleInputChange('client_email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Telefon
                        </label>
                        <input
                          type="text"
                          value={invoice.client_phone || ''}
                          onChange={(e) => handleInputChange('client_phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          NIP
                        </label>
                        <input
                          type="text"
                          value={invoice.client_tax_id || ''}
                          onChange={(e) => handleInputChange('client_tax_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adres
                      </label>
                      <textarea
                        value={invoice.client_address || ''}
                        onChange={(e) => handleInputChange('client_address', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Email Addresses */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Dodatkowe adresy email dla przypomnień
                  </h3>
                  <div className="space-y-3">
                    {additionalEmails.map((email, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="email"
                          value={email}
                          disabled
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeEmail(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Dodaj nowy adres email..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addEmail();
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={addEmail}
                        disabled={!newEmail.trim() || !isValidEmail(newEmail.trim())}
                      >
                        <PlusIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500">
                      Przypomnienia o płatności będą wysyłane na główny adres email oraz wszystkie dodatkowe adresy.
                    </p>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Dodatkowe informacje</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Opis
                      </label>
                      <textarea
                        value={invoice.description || ''}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notatki
                      </label>
                      <textarea
                        value={invoice.notes || ''}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={saving}
                  >
                    Anuluj
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};